import { toApiError } from "@services/api";
const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

/** The person who recorded a load (not necessarily the account owner). */
export type WeightRecordAuthor = {
  id: string;
  name: string | null;
  email: string;
};

/** Where a load was recorded: this dashboard, or the driver app on a phone. */
export type WeightRecordSource = "web" | "mobile";

export type WeightRecord = {
  id: string;
  userId: string; // owner account
  deviceEsn: string;
  start: string; // ISO datetime
  stop: string;  // ISO datetime
  weightKg: number;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  // null for records that predate "Recorded by" (or whose author was deleted).
  // Optional so an older backend that omits them still type-checks.
  recordedBy?: WeightRecordAuthor | null;
  source?: WeightRecordSource;
  clientId?: string | null;
  // Potato-camera totals of the load's window; null without camera data
  // (non-camera machine, driver-app load, loads saved before the camera).
  cvPotatoCount?: number | null;
  cvStoneCount?: number | null; // shown as "Not potatoes"
  cvAvgSizePx?: number | null;
  // Potatoes per size bucket (8 buckets, SIZE_BUCKET_EDGES); null for loads
  // saved before the histogram was kept.
  cvSizeHist?: number[] | null;
};

/** Potato-camera totals of a calculated window (`camera` on the result). */
export type CameraTotals = {
  potatoCount: number;
  stoneCount: number;
  /** Mean potato size (px); null when no potato was sized. */
  avgSizePx: number | null;
  /** Potatoes per size bucket; absent from a backend that predates it. */
  sizeHist?: number[];
};

/** "Recorded by" cell text: name, else email, else an em dash. */
export function recordedByLabel(rec: Pick<WeightRecord, "recordedBy">): string {
  const by = rec.recordedBy;
  if (!by) return "—";
  return by.name?.trim() || by.email?.trim() || "—";
}

export async function fetchWeightRecords(deviceEsn: string): Promise<WeightRecord[]> {
  const params = new URLSearchParams({ deviceEsn });
  const url = `${BASE_URL}/api/v1/weight-records?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch weight records");
  }
  const body = await response.json();
  return body.records ?? [];
}

export async function createWeightRecord(data: {
  deviceEsn: string;
  start: string;
  stop: string;
  weightKg: number;
  comment?: string;
  cvPotatoCount?: number | null;
  cvStoneCount?: number | null;
  cvAvgSizePx?: number | null;
  cvSizeHist?: number[] | null;
}): Promise<WeightRecord> {
  const url = `${BASE_URL}/api/v1/weight-records`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to create weight record");
  }
  return response.json();
}

export async function updateWeightRecord(
  id: string,
  comment: string
): Promise<WeightRecord> {
  const url = `${BASE_URL}/api/v1/weight-records/${id}`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to update weight record");
  }
  return response.json();
}

export async function deleteWeightRecord(id: string): Promise<void> {
  const url = `${BASE_URL}/api/v1/weight-records/${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    throw await toApiError(response, "Failed to delete weight record");
  }
}

export type WeightCalculationProgress = {
  chunkIndex: number;
  totalChunks: number;
  chunkStart: string;
  chunkStop: string;
  chunkWeightKg: number;
  chunkPointCount: number;
  runningWeightKg: number;
  runningPointCount: number;
  calCoeff: number;
};

export type WeightCalculationResult = {
  weightKg: number;
  pointCount: number;
  calCoeff: number;
  /** "johndeere": a John Deere combine with no GreenGrowth calibration — Deere's mass as recorded. */
  calCoeffSource: "manual" | "calibration" | "default" | "johndeere";
  /**
   * Potato-camera totals of the window; null (or absent, from an older
   * backend) for an account without the camera or a window without camera data.
   */
  camera?: CameraTotals | null;
};

/**
 * Stream a weight calculation. `onProgress` fires once per backend chunk.
 * Pass `signal` (from an AbortController) and call `controller.abort()` to
 * cancel — closes the connection and stops further DB work server-side.
 *
 * Throws `DOMException("...", "AbortError")` if the caller aborts.
 */
export async function calculateWeightStream(opts: {
  deviceEsn: string;
  start: string;
  stop: string;
  calCoeff?: number;
  signal?: AbortSignal;
  onProgress?: (p: WeightCalculationProgress) => void;
}): Promise<WeightCalculationResult> {
  const { deviceEsn, start, stop, calCoeff, signal, onProgress } = opts;
  const params = new URLSearchParams({ start, stop });
  if (calCoeff != null) params.set("calCoeff", String(calCoeff));
  const url = `${BASE_URL}/api/v1/weight-records/calculate-stream/${encodeURIComponent(deviceEsn)}?${params}`;

  const response = await fetch(url, {
    headers: { Accept: "text/event-stream" },
    signal,
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to calculate weight");
  }
  if (!response.body) {
    throw new Error("Streaming not supported by this browser");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: WeightCalculationResult | null = null;
  let streamError: string | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const message = parseSSE(raw);
        if (!message) continue;
        if (message.event === "progress" && onProgress) {
          onProgress(message.data as WeightCalculationProgress);
        } else if (message.event === "complete") {
          result = message.data as WeightCalculationResult;
        } else if (message.event === "error") {
          streamError = (message.data as { message?: string }).message ?? "Calculation failed";
        }
      }
    }
  } finally {
    try { reader.cancel(); } catch { /* ignore */ }
  }

  if (streamError) throw new Error(streamError);
  if (!result) throw new Error("Calculation did not complete");
  return result;
}

function parseSSE(raw: string): { event: string; data: unknown } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue; // comment
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;
    const field = line.slice(0, colonIdx);
    const value = line.slice(colonIdx + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return null;
  }
}

export function weightRecordsCsvUrl(deviceEsn: string): string {
  const params = new URLSearchParams({ deviceEsn });
  return `${BASE_URL}/api/v1/weight-records/csv?${params}`;
}
